import { createErrorClass } from '@canvas/error';
import { type DataSecurityPolicy } from '@canvas/frame-api-types';
import { type Action } from '@canvas/model/permissions';
import { type PaywallFeatureStateFromEntitlement } from '@canvas/paywall-types';

export const TokenIdMismatchError = createErrorClass('TokenIdMismatchError');
export const TokenFetchError = createErrorClass('TokenFetchError');

export interface Token {
  /**
   * The board a user is being granted access to.
   * @description an ARI string.
   */
  board: string;
  /**
   * The access grants a user has in this container.
   * @description An ASAP JWT string.
   */
  accessToken: string;
  /**
   * The access grants a user has for media for this contentId.
   * @description A media JWT string.
   */
  mediaToken: string;
  /**
   * The base part of the media api URL that changes depending on env.
   * @description A hostname and protocol for the media API.
   */
  mediaBaseUrl: string;
  /**
   * The media collection this content should be placed in.
   * @description An id.
   */
  mediaCollectionId: string;
  /**
   * optional refers to the feature state not being present or provided in the token
   */
  features?: PaywallFeatureStateFromEntitlement | undefined;
  /**
   * optional since the token may not have a policy due to a contract change in confluence frontend/backend
   */
  dataSecurityPolicy?: DataSecurityPolicy;
}

export type DocumentId = string & { readonly __id: unique symbol };

export type TokenRefresher = () => Promise<Token | undefined>;
export type TokenRefresherV2 = (id?: DocumentId) => Promise<Token | undefined>;
export type TokenRefresherError = (err: unknown) => void;

export type TokenClaims = {
  board: Set<Action>;
};

export type TokenClaimsSynchronizer = (
  id: string | undefined,
  claims: TokenClaims,
) => void;
export type TokenClaimsSynchronizerError = (error: unknown) => void;
export type PaywallFeaturesSynchronizer = (
  featureState: PaywallFeatureStateFromEntitlement,
) => void;

export interface TokenManager {
  refreshToken(initialFetch?: boolean): Promise<void>;
  tokenForDocumentIdOrThrow(documentIdOrAri: string): Promise<Token>;
  updateToken(token: Token | undefined): void;
  token(): Promise<Token>;
  initializeToken(): void;
  onRefreshTokenErrored(handler: TokenRefresherError): void;
  onSynchronizeClaims(handler: TokenClaimsSynchronizer): void;
  onSynchronizeClaimsError(handler: TokenClaimsSynchronizerError): void;
  onPaywallFeatureFetch(handler: PaywallFeaturesSynchronizer): void;
  onRefreshToken(handler: TokenRefresher | TokenRefresherV2): void;
  isEmpty(token: Token | undefined): boolean;
}
